import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRole } from '@/stores/identity';
import { getOwnerCommsLog, getOwnerCommsLogDetailed, type CommsLogRow, type CommsLogDetailedRow } from '@/lib/banners';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

/**
 * /owner/banners/log — chronological audit-ready record of every banner
 * this org has ever sent, with per-banner recipient + ack stats.
 * CSV export is suitable for handing to a college auditor.
 */
export default function OwnerCommsLog() {
  const role = useRole();
  const isOwner = role === 'owner';
  const [rows, setRows] = useState<CommsLogRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    getOwnerCommsLog()
      .then((r) => { if (!cancelled) { setRows(r); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(humanize(err)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [isOwner]);

  const totals = useMemo(() => {
    const total = rows.length;
    const published = rows.filter((r) => r.status === 'published').length;
    const ackRequired = rows.filter((r) => r.acknowledgement_required).length;
    const sumRecipients = rows.reduce((s, r) => s + r.active_recipients, 0);
    const sumAcks = rows.reduce((s, r) => s + r.acknowledged_count, 0);
    const avgAck = sumRecipients > 0 ? Math.round((sumAcks / sumRecipients) * 1000) / 10 : 0;
    return { total, published, ackRequired, avgAck };
  }, [rows]);

  const escape = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  function downloadCsv(filename: string, header: string[], dataRows: unknown[][]) {
    const lines = [header.join(',')];
    for (const r of dataRows) lines.push(r.map(escape).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function onExportSummary() {
    const header = [
      'banner_id', 'title', 'severity', 'status', 'ack_required',
      'target_kind', 'target_roles', 'starts_at', 'ends_at',
      'published_at', 'created_at',
      'active_recipients', 'acknowledged_count', 'dismissed_count', 'ack_rate_pct',
    ];
    const dataRows = rows.map((r) => [
      r.banner_id, r.title, r.severity, r.status, String(r.acknowledgement_required),
      r.target_kind, (r.target_roles ?? []).join('|'),
      r.starts_at, r.ends_at ?? '',
      r.published_at ?? '', r.created_at,
      r.active_recipients, r.acknowledged_count, r.dismissed_count, r.ack_rate_pct,
    ]);
    downloadCsv(`pharmasync-comms-summary-${new Date().toISOString().slice(0, 10)}.csv`, header, dataRows);
  }

  const [exportingAudit, setExportingAudit] = useState(false);
  async function onExportAudit() {
    setExportingAudit(true);
    try {
      const detailed: CommsLogDetailedRow[] = await getOwnerCommsLogDetailed();
      const header = [
        'banner_id', 'banner_title', 'banner_severity', 'banner_status', 'ack_required',
        'target_kind', 'published_at',
        'recipient_user_id', 'recipient_name', 'recipient_email', 'recipient_role',
        'recipient_status', 'acknowledged_at',
        'acknowledged_from_ip', 'acknowledged_user_agent',
      ];
      const dataRows = detailed.map((r) => [
        r.banner_id, r.banner_title, r.banner_severity, r.banner_status,
        String(r.acknowledgement_required), r.target_kind, r.published_at ?? '',
        r.user_id, r.recipient_name, r.recipient_email ?? '', r.recipient_role,
        r.recipient_status, r.acknowledged_at ?? '',
        r.acknowledged_from_ip ?? '', r.acknowledged_user_agent ?? '',
      ]);
      downloadCsv(`pharmasync-comms-audit-${new Date().toISOString().slice(0, 10)}.csv`, header, dataRows);
    } catch (err) {
      setError(humanize(err));
    } finally {
      setExportingAudit(false);
    }
  }

  if (!isOwner) {
    return <div className="container mx-auto py-8 px-4"><p className="text-sm text-muted-foreground">Owner only.</p></div>;
  }
  if (loading) return <p className="container mx-auto py-8 px-4 text-sm text-muted-foreground">Loading…</p>;
  if (error)   return <p className="container mx-auto py-8 px-4 text-sm text-red-600">{error}</p>;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <Link to="/owner/banners" className="text-xs text-muted-foreground hover:underline">← Back to communications</Link>
            <h1 className="text-2xl font-semibold tracking-tight">Communications log</h1>
            <p className="text-sm text-muted-foreground">
              Every banner your org has ever sent, with recipient counts and acknowledgement rates.
              Suitable for college audits and internal compliance reviews.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onExportSummary}
              disabled={rows.length === 0}
              className="rounded-md border border-border bg-card px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50"
              title="One row per banner (aggregate counts)."
            >
              Export summary
            </button>
            <button
              type="button"
              onClick={onExportAudit}
              disabled={rows.length === 0 || exportingAudit}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              title="One row per (banner × staff member) with name, role, status, and timestamp — audit-ready."
            >
              {exportingAudit ? 'Preparing…' : 'Export audit CSV'}
            </button>
          </div>
        </div>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Total banners" value={String(totals.total)} />
          <Tile label="Published" value={String(totals.published)} />
          <Tile label="Ack required" value={String(totals.ackRequired)} />
          <Tile label="Avg ack rate" value={`${totals.avgAck}%`} />
        </section>

        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-lg border border-dashed border-border p-8 text-center">
            No banners yet. Compose your first one from <Link to="/owner/banners" className="underline">Communications</Link>.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Severity</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Audience</th>
                  <th className="px-3 py-2 text-left">Published</th>
                  <th className="px-3 py-2 text-right">Recipients</th>
                  <th className="px-3 py-2 text-right">Acked</th>
                  <th className="px-3 py-2 text-right">Ack rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const ackRatePct = Number(r.ack_rate_pct);
                  const ackColor =
                    !r.acknowledgement_required ? 'text-muted-foreground' :
                    ackRatePct >= 80 ? 'text-emerald-700' :
                    ackRatePct >= 50 ? 'text-amber-700' :
                                       'text-red-700';
                  return (
                    <tr key={r.banner_id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <Link
                          to={r.acknowledgement_required
                            ? `/owner/banners/${r.banner_id}/recipients`
                            : `/owner/banners/${r.banner_id}`}
                          className="font-medium hover:underline"
                        >
                          {r.title}
                        </Link>
                        {r.acknowledgement_required && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                            ack required
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">{r.severity}</td>
                      <td className="px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">{r.status}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {r.target_kind === 'my_org_all' && 'All staff'}
                        {r.target_kind === 'my_org_roles' && (r.target_roles ?? []).join(', ')}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                        {r.published_at ? formatDate(r.published_at) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">{r.active_recipients}</td>
                      <td className="px-3 py-2 text-right">{r.acknowledged_count}</td>
                      <td className={`px-3 py-2 text-right font-medium ${ackColor}`}>
                        {r.acknowledgement_required ? `${ackRatePct.toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Recipient counts reflect active staff at the time of viewing — a banner sent before
          new staff joined may show a higher pool than when it was published. For point-in-time
          audit evidence, cross-reference with audit_logs (BANNER_PUBLISHED, BANNER_ACKNOWLEDGED).
        </p>
      </div>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-3xl font-semibold mt-1">{value}</div>
    </div>
  );
}
